import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Save, FileText, Bot, Sun, Moon, Settings, MessageSquare, Plus, History, Sparkles } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const HeaderContainer = styled(motion.header)`
  height: 60px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(17,18,23,0.65)' : 'rgba(255,255,255,0.65)'};
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  padding: 8px 16px;
  border-radius: 16px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(15,16,20,0.6)' : 'rgba(255,255,255,0.8)'};
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  line-height: 1.2;
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  .logo-icon {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    background: linear-gradient(135deg, #0A84FF 0%, #7C3AED 50%, #30D158 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(10,132,255,0.3);
    position: relative;
    overflow: hidden;
  }

  .logo-icon::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent);
    animation: shine 3s infinite;
  }

  @keyframes shine {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    50% { transform: translateX(100%) translateY(100%) rotate(45deg); }
    100% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
  }

  /* 그라데이션 텍스트 느낌 */
  .brand-text {
    background: linear-gradient(135deg, #0A84FF 0%, #64D2FF 50%, #30D158 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-weight: 800;
    letter-spacing: -0.5px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 14px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Button = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: 1px solid ${props => props.variant === 'primary' ? 'transparent' : (props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)')};
  border-radius: 12px;
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary : (props.theme.mode === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.8)')};
  color: ${props => props.variant === 'primary' ? '#ffffff' : (props.theme.mode === 'dark' ? '#E5E7EB' : '#111827')};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: transform .14s cubic-bezier(.22,.61,.36,1), background-color .22s cubic-bezier(.22,.61,.36,1), box-shadow .22s cubic-bezier(.22,.61,.36,1), color .2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0,0,0,0.16);
  }

  &:active {
    transform: translateY(0);
  }

  /* 아이콘이 텍스트 색상과 동기화 */
  svg { color: inherit; stroke: currentColor; transition: color .2s ease, opacity .2s ease; opacity: .95; }
  &:hover svg { opacity: 1; }
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$saved ? '#28a745' : '#ffc107'};
  margin-left: 8px;
`;

function Header({ currentFile, onSave, onToggleAI, onToggleSidebarChat, onOpenAPISettings, onNewConversation, onOpenRecentFiles }) {
  const { theme, toggleTheme } = useTheme();
  const [isSaved, setIsSaved] = React.useState(true);

  const handleSave = async () => {
    setIsSaved(false);
    try {
      await onSave();
      setIsSaved(true);
    } catch (error) {
      console.error('저장 실패:', error);
    }
  };

  const getFileName = () => {
    if (currentFile) {
      return currentFile.split(/[\\/]/).pop();
    }
    return '새 문서';
  };

  return (
    <HeaderContainer
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.26, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <Logo onClick={() => { window.location.href = '/landing.html'; }} style={{ cursor: 'pointer' }} title="랜딩 페이지로 이동">
        <div className="logo-icon">
          <Sparkles size={16} color="white" />
        </div>
        <span className="brand-text">One-Line Paper</span>
      </Logo>

      <FileInfo>
        <FileText size={16} />
        {getFileName()}
        <StatusIndicator $saved={isSaved} />
      </FileInfo>

      <ActionButtons>
        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          variant="primary"
        >
          <Save size={16} />
          저장
        </Button>

        
        

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onToggleSidebarChat}
        >
          <MessageSquare size={16} />
          AI 글 작성
        </Button>

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onNewConversation}
        >
          <Plus size={16} />
          새 대화
        </Button>

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onOpenRecentFiles}
        >
          <History size={16} />
          최근 파일
        </Button>

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onOpenAPISettings}
        >
          <Settings size={16} />
        </Button>

        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleTheme}
        >
          {theme.mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </Button>
      </ActionButtons>
    </HeaderContainer>
  );
}

export default Header;
